import { useState } from 'react';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDown, Paintbrush } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SplitStyleSelectorProps {
    currentStyle: ManuscriptMarkStyle;
    onApply: (styleId: string) => void;
    children: React.ReactNode;
}

export function SplitStyleSelector({ currentStyle, onApply, children }: SplitStyleSelectorProps) {
    const [open, setOpen] = useState(false);

    const handleApply = () => {
        onApply(currentStyle.id);
    };

    const isDarkMode = useDarkMode();

    return (
        <div className={cn(
            "flex items-stretch overflow-hidden rounded-xl border transition-all duration-300",
            "shadow-[0_4px_20px_rgb(0,0,0,0.03)] hover:shadow-[0_4px_20px_rgb(0,0,0,0.06)]",
            isDarkMode
                ? "bg-white/5 border-white/5 backdrop-blur-md"
                : "bg-black/5 border-black/5 backdrop-blur-md"
        )}>
            {/* Main apply button */}
            <Button
                onClick={handleApply}
                variant="ghost"
                className="flex-1 h-11 justify-start gap-3 rounded-none bg-transparent px-4 hover:bg-muted/30 transition-all active:scale-[0.98] group"
            >
                <div className={cn(
                    "w-8 h-8 rounded-lg flex items-center justify-center transition-all group-hover:rotate-6",
                    isDarkMode ? "bg-white/5 border border-white/10" : "bg-black/5 border border-black/5"
                )}>
                    <Paintbrush className="h-4 w-4 text-muted-foreground/70 group-hover:text-primary transition-colors" />
                </div>

                <div className="flex flex-col items-start gap-0 min-w-0">
                    <span className="text-[14px] font-bold py-0.5 truncate max-w-full">
                        <span
                            className={`mark mark-texture-${currentStyle.texture} mark-${currentStyle.type} mark-is-start mark-is-end`}
                            style={{
                                '--mark-color': currentStyle.color.includes('rgba')
                                    ? currentStyle.color
                                    : currentStyle.color.replace('rgb', 'rgba').replace(')', ', 0.35)'),
                                '--mark-thickness': currentStyle.thickness ? `${currentStyle.thickness}px` : '2px'
                            } as any}
                        >
                            {currentStyle.name}
                        </span>
                    </span>
                </div>
            </Button>

            <div className="w-[1px] h-7 self-center bg-muted-foreground/10" />

            {/* Dropdown trigger */}
            <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        className="h-11 w-10 p-0 rounded-none bg-transparent hover:bg-muted/30 transition-colors"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <ChevronDown className={cn(
                            "h-4 w-4 text-muted-foreground/50 transition-transform duration-300",
                            open ? "rotate-180 text-primary" : "group-hover:text-muted-foreground"
                        )} />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align="end"
                    className={cn(
                        "w-[320px] shadow-2xl mt-2 p-1 rounded-xl backdrop-blur-xl border",
                        isDarkMode
                            ? "bg-zinc-900/80 border-white/10"
                            : "bg-white/80 border-zinc-200"
                    )}
                >
                    {children}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}
